import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconPlus,
  IconTrash,
  IconFileInvoice,
  IconCreditCard,
  IconCategory,
  IconFilter,
  IconX,
  IconCheck,
  IconCash,
  IconTruckDelivery,
  IconAlertCircle
} from "@tabler/icons-react";
import { iconStroke } from "../config/config";
import { useExpenses, useExpenseCategories, createExpense, createExpenseCategory, deleteExpense, settleExpenseDue } from "../controllers/expense.controller";
import { usePaymentTypes } from "../controllers/settings.controller";
import { useSuppliers } from "../controllers/inventory.controller";
import { toast } from "react-hot-toast";

const getDateRange = (filterKey, customFrom = null, customTo = null) => {
  const now = new Date();
  const getFormattedDate = (date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  };

  const todayStr = getFormattedDate(now);

  switch (filterKey) {
    case "today":
      return { from: todayStr, to: todayStr };
    case "yesterday": {
      const yesterday = new Date();
      yesterday.setDate(now.getDate() - 1);
      const yesterdayStr = getFormattedDate(yesterday);
      return { from: yesterdayStr, to: yesterdayStr };
    }
    case "tomorrow": {
      const tomorrow = new Date();
      tomorrow.setDate(now.getDate() + 1);
      const tomorrowStr = getFormattedDate(tomorrow);
      return { from: tomorrowStr, to: tomorrowStr };
    }
    case "last_7days": {
      const last7 = new Date();
      last7.setDate(now.getDate() - 7);
      return { from: getFormattedDate(last7), to: todayStr };
    }
    case "this_month": {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { from: getFormattedDate(firstDay), to: getFormattedDate(lastDay) };
    }
    case "last_month": {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: getFormattedDate(firstDay), to: getFormattedDate(lastDay) };
    }
    case "custom":
      return { from: customFrom, to: customTo };
    default:
      return { from: todayStr, to: todayStr };
  }
};

export default function ExpensesPage() {
  const { t } = useTranslation();

  const filters = [
    { key: "today", value: t("invoices.today") },
    { key: "tomorrow", value: t("invoices.tomorrow") },
    { key: "yesterday", value: t("invoices.yesterday") },
    { key: "last_7days", value: t("invoices.last_7days") },
    { key: "this_month", value: t("invoices.this_month") },
    { key: "last_month", value: t("invoices.last_month") },
    { key: "custom", value: t("invoices.custom") },
  ];

  const filterTypeRef = useRef();
  const fromDateRef = useRef();
  const toDateRef = useRef();

  const now = new Date();
  const defaultDateFrom = `${now.getFullYear()}-${(now.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;
  const defaultDateTo = `${now.getFullYear()}-${(now.getMonth() + 2)
    .toString()
    .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;

  const [filter, setFilter] = useState("today");
  const [fromDate, setFromDate] = useState(defaultDateFrom);
  const [toDate, setToDate] = useState(defaultDateTo);

  const [expenseDate, setExpenseDate] = useState(defaultDateFrom);
  const [categoryId, setCategoryId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [newCategoryName, setNewCategoryName] = useState("");

  // Settle Due Modal State
  const [selectedSettleExpense, setSelectedSettleExpense] = useState(null);
  const [settleMethod, setSettleMethod] = useState("Cash");
  const [settleDate, setSettleDate] = useState(defaultDateFrom);
  const [settleNotes, setSettleNotes] = useState("");
  const [settleLoading, setSettleLoading] = useState(false);

  const computedDateRange = getDateRange(filter, fromDate, toDate);
  const { data: expenses, mutateExpenses } = useExpenses(computedDateRange.from, computedDateRange.to);
  const { data: categories, mutatePurchases: mutateCategories } = useExpenseCategories();
  const { data: paymentTypes } = usePaymentTypes();
  const { data: suppliers } = useSuppliers();

  const handleAddExpense = async () => {
    if (!amount || !expenseDate) {
      toast.error("Please provide amount and date!");
      return;
    }

    if (paymentMethod === "Due" && !supplierId) {
      toast.error("Please select a Supplier for Due expenses!");
      return;
    }

    try {
      toast.loading("Recording expense...");
      const res = await createExpense({
        category_id: categoryId || null,
        supplier_id: supplierId || null,
        amount,
        description,
        date: expenseDate,
        payment_method: paymentMethod
      });
      toast.dismiss();
      toast.success("Expense recorded successfully!");
      mutateExpenses();
      document.getElementById("modal-add-expense").close();
      // Reset
      setAmount(0);
      setDescription("");
      setSupplierId("");
      setPaymentMethod("Cash");
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error saving expense");
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName) return;
    try {
      await createExpenseCategory(newCategoryName);
      toast.success("Category added!");
      setNewCategoryName("");
      mutateCategories();
    } catch (error) {
      toast.error("Error adding category");
    }
  };

  const handleDeleteExpense = async (id) => {
    if(!window.confirm("Are you sure you want to delete this expense?")) return;
    try {
      await deleteExpense(id);
      toast.success("Expense deleted");
      mutateExpenses();
    } catch (error) {
      toast.error("Error deleting expense");
    }
  };

  const openSettleModal = (exp) => {
    setSelectedSettleExpense(exp);
    setSettleDate(new Date().toISOString().substring(0, 10));
    setSettleNotes(`Paid to ${exp.supplier_name || 'Supplier'}`);
    setSettleMethod(paymentTypes && paymentTypes.length > 0 ? paymentTypes[0].title : "Cash");
    document.getElementById("modal-settle-expense").showModal();
  };

  const handleProcessSettle = async () => {
    if (!selectedSettleExpense) return;
    try {
      setSettleLoading(true);
      toast.loading("Settling due expense...");
      await settleExpenseDue(selectedSettleExpense.id, {
        payment_method: settleMethod,
        date: settleDate,
        notes: settleNotes
      });
      toast.dismiss();
      toast.success("Expense settled successfully!");
      mutateExpenses();
      document.getElementById("modal-settle-expense").close();
      setSelectedSettleExpense(null);
    } catch (error) {
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Error settling expense");
    } finally {
      setSettleLoading(false);
    }
  };

  // Build payment methods list: settings types + "Due"
  const availablePaymentMethods = [];
  if (paymentTypes && Array.isArray(paymentTypes)) {
    paymentTypes.forEach(pt => {
      if (pt.is_active === 1 || pt.is_active === true || pt.is_active === undefined) {
        availablePaymentMethods.push(pt.title);
      }
    });
  }
  if (availablePaymentMethods.length === 0) {
    availablePaymentMethods.push("Cash", "Bank Transfer");
  }
  if (!availablePaymentMethods.includes("Due")) {
    availablePaymentMethods.push("Due");
  }

  return (
    <Page>
      <div className="flex flex-wrap gap-4 flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold italic text-restro-green-dark">General Expenses</h3>
          <p className="text-sm text-gray-500 mt-1">
            Showing Expenses for {filters.find(f => f.key === filter)?.value || filter}
          </p>
        </div>
        <div className="flex items-center gap-2">
            <button
              onClick={() => document.getElementById("filter-dialog").showModal()}
              className="w-8 h-8 flex items-center justify-center text-gray-400 rounded-full active:scale-95 transition hover:bg-restro-button-hover"
              title="Filter Expenses"
            >
              <IconFilter />
            </button>
            <button onClick={() => document.getElementById("modal-categories").showModal()} className="btn btn-outline btn-sm flex items-center gap-1">
                <IconCategory size={18} /> Categories
            </button>
            <button onClick={() => document.getElementById("modal-add-expense").showModal()} className="btn btn-primary btn-sm flex items-center gap-1">
                <IconPlus size={18} /> New Expense
            </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white dark:bg-restro-gray border border-restro-border-green rounded-2xl">
        <table className="table w-full">
          <thead>
            <tr className="bg-restro-bg-gray">
              <th>ID</th>
              <th>Date</th>
              <th>Category</th>
              <th>Supplier</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Method</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {(!expenses || expenses.length === 0) && (
              <tr>
                <td colSpan="9" className="text-center py-8 text-gray-500">
                  No expenses found for the selected period.
                </td>
              </tr>
            )}
            {expenses?.map((e) => {
              const isDue = e.payment_status === 'due' || String(e.payment_method).toLowerCase() === 'due';
              return (
                <tr key={e.id} className={isDue ? "bg-amber-50/40 dark:bg-amber-950/10" : ""}>
                  <td>#{e.id}</td>
                  <td>{new Date(e.date).toLocaleDateString()}</td>
                  <td>
                    <span className="badge badge-sm badge-ghost font-medium">{e.category_name || "Uncategorized"}</span>
                  </td>
                  <td>
                    {e.supplier_name ? (
                      <span className="flex items-center gap-1 text-sm font-semibold text-gray-800 dark:text-gray-200">
                        <IconTruckDelivery size={15} className="text-gray-400" />
                        {e.supplier_name}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="text-sm max-w-xs truncate" title={e.description}>{e.description || '-'}</td>
                  <td className="font-bold text-red-500">{Number(String(e.amount).replace(/,/g, "")).toFixed(2)}</td>
                  <td>
                    {isDue ? (
                      <span className="badge badge-warning badge-sm font-bold gap-1 text-amber-900 bg-amber-200">
                        <IconAlertCircle size={12} /> Due
                      </span>
                    ) : (
                      <span className="badge badge-success badge-sm font-bold gap-1 text-emerald-900 bg-emerald-200">
                        <IconCheck size={12} /> Paid
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                      {e.payment_method}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      {isDue && (
                        <button 
                          onClick={() => openSettleModal(e)} 
                          className="btn btn-xs btn-success text-white font-bold flex items-center gap-1"
                          title="Settle this due expense"
                        >
                          <IconCash size={14} /> Settle
                        </button>
                      )}
                      <button onClick={() => handleDeleteExpense(e.id)} className="btn btn-ghost btn-xs text-red-500" title="Delete">
                        <IconTrash size={16}/>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Expense Modal */}
      <dialog id="modal-add-expense" className="modal">
        <div className="modal-box border border-restro-border-green max-w-lg">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><IconFileInvoice /> Record New Expense</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-text mb-1 block text-gray-500 font-semibold">Date *</label>
                <input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} className="input input-bordered w-full" />
              </div>
              <div>
                <label className="label-text mb-1 block text-gray-500 font-semibold">Amount *</label>
                <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="input input-bordered w-full font-bold text-lg" placeholder="0.00" />
              </div>
            </div>

            <div>
              <label className="label-text mb-1 block text-gray-500 font-semibold">Category</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="select select-bordered w-full">
                <option value="">Select Category (Optional)</option>
                {categories?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            {/* Supplier Selection */}
            <div>
              <label className="label-text mb-1 flex items-center justify-between text-gray-500 font-semibold">
                <span>Supplier / Payee {paymentMethod === 'Due' && <span className="text-red-500 font-bold">* (Required for Due)</span>}</span>
                <span className="text-xs text-gray-400">Track balance in Accounts Payable</span>
              </label>
              <select 
                value={supplierId} 
                onChange={(e) => setSupplierId(e.target.value)} 
                className={`select select-bordered w-full ${paymentMethod === 'Due' && !supplierId ? 'border-amber-400 bg-amber-50/20' : ''}`}
              >
                <option value="">No Supplier (Direct / Walk-in)</option>
                {suppliers?.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} {s.phone ? `(${s.phone})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Method selection including Due */}
            <div>
              <label className="label-text mb-1 block text-gray-500 font-semibold">Payment Type</label>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="select select-bordered w-full">
                {availablePaymentMethods.map((pm, idx) => (
                  <option key={idx} value={pm} className={pm === 'Due' ? 'font-bold text-amber-700' : ''}>
                    {pm === 'Due' ? '⚠️ Due (Credit / Accounts Payable)' : pm}
                  </option>
                ))}
              </select>
              {paymentMethod === 'Due' && (
                <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg mt-1 border border-amber-200">
                  ℹ️ This will NOT deduct cash today. It will be recorded as a Liability (Accounts Payable) under the selected supplier.
                </p>
              )}
            </div>

            <div>
              <label className="label-text mb-1 block text-gray-500 font-semibold">Description / Notes</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="textarea textarea-bordered w-full" placeholder="What was this expense for?"></textarea>
            </div>
          </div>
          <div className="modal-action">
            <button type="button" onClick={handleAddExpense} className="btn btn-primary w-full">Save Expense</button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop"><button>close</button></form>
      </dialog>

      {/* Settle Expense Due Modal */}
      <dialog id="modal-settle-expense" className="modal">
        <div className="modal-box border border-restro-border-green max-w-md">
          <h3 className="font-bold text-lg mb-3 flex items-center gap-2 text-emerald-800">
            <IconCash /> Settle Due Expense #{selectedSettleExpense?.id}
          </h3>
          {selectedSettleExpense && (
            <div className="space-y-4">
              <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Supplier:</span>
                  <span className="font-bold text-gray-900">{selectedSettleExpense.supplier_name || 'Unknown'}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-600">Category:</span>
                  <span className="font-semibold text-gray-800">{selectedSettleExpense.category_name || 'Expense'}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-600">Due Amount:</span>
                  <span className="font-black text-rose-600 text-base">
                    {Number(String(selectedSettleExpense.due_amount || selectedSettleExpense.amount || 0).replace(/,/g, "")).toFixed(2)}
                  </span>
                </div>
              </div>

              <div>
                <label className="label-text mb-1 block text-gray-500 font-semibold">Payment Date</label>
                <input 
                  type="date" 
                  value={settleDate} 
                  onChange={(e) => setSettleDate(e.target.value)} 
                  className="input input-bordered w-full" 
                />
              </div>

              <div>
                <label className="label-text mb-1 block text-gray-500 font-semibold">Paid Via</label>
                <select 
                  value={settleMethod} 
                  onChange={(e) => setSettleMethod(e.target.value)} 
                  className="select select-bordered w-full"
                >
                  {availablePaymentMethods.filter(m => m !== 'Due').map((m, idx) => (
                    <option key={idx} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-text mb-1 block text-gray-500 font-semibold">Payment Notes</label>
                <input 
                  type="text" 
                  value={settleNotes} 
                  onChange={(e) => setSettleNotes(e.target.value)} 
                  className="input input-bordered w-full" 
                  placeholder="Receipt no, cheque no, etc."
                />
              </div>

              <div className="modal-action">
                <button 
                  type="button" 
                  onClick={handleProcessSettle} 
                  disabled={settleLoading}
                  className="btn btn-success text-white w-full font-bold"
                >
                  {settleLoading ? "Settling..." : "Confirm & Settle Due"}
                </button>
              </div>
            </div>
          )}
        </div>
        <form method="dialog" className="modal-backdrop"><button>close</button></form>
      </dialog>

      {/* Categories Modal */}
      <dialog id="modal-categories" className="modal">
        <div className="modal-box border border-restro-border-green">
          <h3 className="font-bold text-lg mb-4">Expense Categories</h3>
          <div className="flex gap-2 mb-4">
            <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="New Category..." className="input input-bordered input-sm flex-1" />
            <button onClick={handleAddCategory} className="btn btn-sm btn-primary">Add</button>
          </div>
          <div className="space-y-1">
            {categories?.map(c => (
              <div key={c.id} className="p-2 bg-gray-50 dark:bg-restro-bg-hover-dark-mode rounded flex justify-between items-center text-sm">
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
        <form method="dialog" className="modal-backdrop"><button>close</button></form>
      </dialog>

      {/* filter dialog */}
      <dialog id="filter-dialog" className="modal">
        <div className="modal-box border border-restro-border-green dark:rounded-2xl">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <IconFilter stroke={iconStroke} /> {t('invoices.filter')}
          </h3>
          {/* filters */}
          <div className="my-4">
            <div>
              <label className=" block text-gray-500 text-sm">{t('invoices.filter')}</label>
              <select
                className="text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green"
                ref={filterTypeRef}
                defaultValue={filter}
              >
                {filters.map((f, index) => (
                  <option key={index} value={f.key}>
                    {f.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 mt-4">
              <div className="flex-1">
                <label
                  htmlFor="fromDate"
                  className=" block text-gray-500 text-sm"
                >
                  {t('invoices.from')}
                </label>
                <input
                  defaultValue={fromDate}
                  type="date"
                  ref={fromDateRef}
                  className="text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green"
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="toDate"
                  className=" block text-gray-500 text-sm"
                >
                  {t('invoices.to')}
                </label>
                <input
                  defaultValue={toDate}
                  type="date"
                  ref={toDateRef}
                  className="text-sm w-full rounded-lg px-4 py-2 border border-restro-border-green dark:bg-black focus:outline-restro-border-green"
                />
              </div>
            </div>
          </div>
          {/* filters */}
          <div className="modal-action">
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className="btn transition active:scale-95 hover:shadow-lg px-4 py-3 flex-1 items-center justify-center align-center rounded-xl border border-restro-border-green bg-restro-card-bg hover:bg-restro-button-hover text-restro-text">{t('invoices.close')}</button>
              <button onClick={()=>{
                setFilter(filterTypeRef.current.value);
                setFromDate(fromDateRef.current.value || null);
                setToDate(toDateRef.current.value || null);
              }}  className="rounded-xl transition active:scale-95 hover:shadow-lg px-4 py-3 text-white ml-3 border border-restro-border-green bg-restro-green hover:bg-restro-green-button-hover">{t('invoices.apply')}</button>
            </form>
          </div>
        </div>
      </dialog>
      {/* filter dialog */}
    </Page>
  );
}
